import { Platform } from "react-native";
import * as FileSystem from "expo-file-system/legacy";
import * as Sharing from "expo-sharing";

export type CsvColumn<T> = {
  header: string;
  value: (row: T) => unknown;
};

const isDateLike = (value: unknown): value is { toDate: () => Date } =>
  !!value &&
  typeof value === "object" &&
  "toDate" in value &&
  typeof (value as { toDate?: unknown }).toDate === "function";

export const formatExportValue = (value: unknown): string => {
  if (value === null || typeof value === "undefined") return "";
  if (isDateLike(value)) return value.toDate().toLocaleString("ru-RU");
  if (value instanceof Date) return value.toLocaleString("ru-RU");

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(formatExportValue).filter(Boolean).join("; ");
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const displayValue =
      record.name ??
      record.fullName ??
      record.title ??
      record.label ??
      record.productName ??
      record.uri ??
      record.url ??
      record.id;

    if (displayValue) return formatExportValue(displayValue);

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

const escapeCsvCell = (value: unknown): string => {
  const stringValue = formatExportValue(value);
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const buildCsvContent = <T>(columns: CsvColumn<T>[], rows: T[]) => {
  const header = columns.map((column) => escapeCsvCell(column.header));
  const body = rows.map((row) =>
    columns.map((column) => escapeCsvCell(column.value(row))),
  );

  return [header, ...body].map((csvRow) => csvRow.join(",")).join("\n");
};

export const downloadCsvFile = async (fileName: string, csvContent: string) => {
  const normalizedFileName = fileName.endsWith(".csv")
    ? fileName
    : `${fileName}.csv`;

  if (Platform.OS === "web" && typeof document !== "undefined") {
    const blob = new Blob(["\uFEFF", csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = normalizedFileName;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    return;
  }

  const sharingAvailable = await Sharing.isAvailableAsync();
  if (!sharingAvailable) {
    throw new Error("Sharing is not available on this device");
  }

  const directory = FileSystem.cacheDirectory ?? FileSystem.documentDirectory;
  if (!directory) {
    throw new Error("File system directory is not available");
  }

  const fileUri = `${directory}${normalizedFileName}`;
  await FileSystem.writeAsStringAsync(fileUri, `\uFEFF${csvContent}`, {
    encoding: FileSystem.EncodingType.UTF8,
  });

  await Sharing.shareAsync(fileUri, {
    mimeType: "text/csv",
    dialogTitle: normalizedFileName,
    UTI: "public.comma-separated-values-text",
  });
};
